import { Dispatch, SetStateAction, useMemo } from 'react';

import { Column } from '@tanstack/react-table';
import { useParams } from 'react-router-dom';

import {
  getInconsistencySourceDisplayName,
  getInconsistencyTypeDisplayName,
} from '../../../../../backend/src/matter/inconsistency/inconsistency-display';
import { Inconsistency } from '../../../../../backend/src/matter/inconsistency/inconsistency.entity';
import { capitalizeFirstLetter } from '@/helpers/filterNames';
import { DocumentFilter } from '@/new/components/document-filter';
import { FilterMultiSelect } from '@/new/components/filter/filter-multi-select';
import { FilterPopoverContent } from '@/new/components/filter-popover-content';
import { Switch } from '@/new/components/shadcn/switch';
import { MyOptionType } from '@/types';

export interface InconsistencyGlobalFilter {
  query: string;
  hasRelevantFactsFilter: boolean;
  includedDocs: Map<string, MyOptionType>;
  excludedDocs: Map<string, MyOptionType>;
}

export function FilterPopover({
  inconsistencies,
  typeColumn,
  levelColumn,
  sourceColumn,
  globalFilter,
  setGlobalFilter,
  clearAllFilters,
}: {
  inconsistencies: Inconsistency[];
  typeColumn?: Column<Inconsistency, unknown>;
  levelColumn?: Column<Inconsistency, unknown>;
  sourceColumn?: Column<Inconsistency, unknown>;
  globalFilter: InconsistencyGlobalFilter;
  setGlobalFilter: Dispatch<SetStateAction<InconsistencyGlobalFilter>>;
  clearAllFilters: () => void;
}) {
  const { matterId } = useParams();
  if (!matterId) return null;
  const handleDocsChange = ({
    includedDocs,
    excludedDocs,
  }: {
    includedDocs: Map<string, MyOptionType> | undefined;
    excludedDocs: Map<string, MyOptionType> | undefined;
  }) => {
    setGlobalFilter((prev) => ({
      ...prev,
      includedDocs: includedDocs ?? prev.includedDocs,
      excludedDocs: excludedDocs ?? prev.excludedDocs,
    }));
  };

  const types = useMemo(() => Array.from(new Set(inconsistencies.map((i) => i.type))), [inconsistencies]);
  const levels = useMemo(() => Array.from(new Set(inconsistencies.map((i) => i.level))), [inconsistencies]);
  const sources = useMemo(() => Array.from(new Set(inconsistencies.map((i) => i.source))), [inconsistencies]);

  const typeFilterValue = typeColumn?.getFilterValue();
  const selectedTypes = Array.isArray(typeFilterValue) ? typeFilterValue : [];

  const levelFilterValue = levelColumn?.getFilterValue();
  const selectedLevels = Array.isArray(levelFilterValue) ? levelFilterValue : [];

  const sourceFilterValue = sourceColumn?.getFilterValue();
  const selectedSources = Array.isArray(sourceFilterValue) ? sourceFilterValue : [];

  return (
    <FilterPopoverContent clearAllFilters={clearAllFilters}>
      <label className="flex gap-4 text-gray-600">
        <Switch
          checked={globalFilter.hasRelevantFactsFilter}
          onCheckedChange={(checked) => setGlobalFilter({ ...globalFilter, hasRelevantFactsFilter: checked })}
        />
        <p>Contains Relevant Facts</p>
      </label>

      <div>
        {typeColumn && (
          <>
            <p className="mb-3 text-gray-600">Type</p>
            <FilterMultiSelect
              values={selectedTypes}
              options={types}
              column={typeColumn}
              placeholder="Select inconsistency types..."
              formatLabel={getInconsistencyTypeDisplayName}
            />
          </>
        )}
      </div>

      <div>
        {levelColumn && (
          <>
            <p className="mb-3 text-gray-600">Level</p>
            <FilterMultiSelect
              values={selectedLevels}
              options={levels}
              column={levelColumn}
              placeholder="Select inconsistency levels..."
              formatLabel={capitalizeFirstLetter}
            />
          </>
        )}
      </div>

      <div>
        {sourceColumn && (
          <>
            <p className="mb-3 text-gray-600">Source</p>
            <FilterMultiSelect
              values={selectedSources}
              options={sources}
              column={sourceColumn}
              placeholder="Select source types..."
              formatLabel={getInconsistencySourceDisplayName}
            />
          </>
        )}
      </div>
      <div>
        <p className="mb-3 text-gray-600">Documents</p>
        <DocumentFilter
          includedDocs={globalFilter.includedDocs}
          excludedDocs={globalFilter.excludedDocs}
          handleDocsChange={handleDocsChange}
          mode="include-facts-from"
        />
      </div>
    </FilterPopoverContent>
  );
}
